/**
 * P2.6-2: Activity Ledger Service
 *
 * Frontend client for consuming the unified activity ledger.
 * This is the ONLY way staff UI should access operational data.
 */

import { ActivityRecord, ActivityQuery, LedgerStats } from '@/types/activityLedger';
import { API_BASE } from '@/config/api';

console.log('[OPS_FEED] Activity Ledger Service initialized');

/**
 * Fetch activities from the unified ledger
 *
 * @param filters - Query filters (room, type, status, etc.)
 * @returns Array of activity records
 */
export async function fetchActivityLedger(filters?: ActivityQuery): Promise<ActivityRecord[]> {
  const params = new URLSearchParams();

  if (filters?.room) params.append('room', filters.room);
  if (filters?.stayId) params.append('stayId', filters.stayId);
  if (filters?.type) params.append('type', filters.type);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.from) params.append('from', filters.from);
  if (filters?.to) params.append('to', filters.to);
  if (filters?.limit) params.append('limit', filters.limit.toString());

  const url = `${API_BASE}/activity-ledger${params.toString() ? '?' + params.toString() : ''}`;

  console.log('[OPS_FEED] Fetching activities:', { filters, url });

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch activity ledger: ${response.statusText}`);
  }

  const data = await response.json();

  console.log(`[OPS_FEED] Loaded ${data.activities?.length || 0} activities`);

  return data.activities || [];
}

/**
 * Fetch activities for a specific stay
 *
 * @param stayId - The stay ID
 * @returns Array of activity records
 */
export async function fetchActivityByStay(stayId: string): Promise<ActivityRecord[]> {
  const url = `${API_BASE}/activity-ledger/stay/${stayId}`;

  console.log('[OPS_FEED] Fetching stay activities:', stayId);

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch stay activities: ${response.statusText}`);
  }

  const data = await response.json();

  return data.activities || [];
}

/**
 * Fetch ledger diagnostics
 *
 * @returns Ledger statistics
 */
export async function fetchLedgerStats(): Promise<LedgerStats> {
  const url = `${API_BASE}/activity-ledger/diagnostics`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch ledger stats: ${response.statusText}`);
  }

  const data = await response.json();

  return data.stats;
}

/**
 * Room directory entry from PMS
 */
export interface RoomDirectoryEntry {
  roomId: string;
  roomNumber: string;
  roomType: string;
  floor: number;
  status: 'VACANT' | 'RESERVED' | 'OCCUPIED' | 'CLEANING' | 'OUT_OF_SERVICE';
  stay: {
    stayId: string;
    checkInDate: string;
    checkOutDate: string;
    actualCheckIn: string | null;
    actualCheckOut: string | null;
    status: string;
  } | null;
  guest: {
    guestId: string;
    firstName: string;
    lastName: string;
    email: string | null;
    phone: string | null;
    language: string;
  } | null;
  upcomingStay: {
    stayId: string;
    checkInDate: string;
    checkOutDate: string;
    actualCheckIn: string | null;
    actualCheckOut: string | null;
    status: string;
  } | null;
  upcomingGuest: {
    guestId: string;
    firstName: string;
    lastName: string;
    email: string | null;
    phone: string | null;
    language: string;
  } | null;
}

/**
 * A single past stay entry from room history
 */
export interface RoomHistoryEntry {
  stayId: string;
  guestId: string;
  guestName: string;
  guestEmail: string | null;
  checkInDate: string;
  checkOutDate: string;
  actualCheckIn: string | null;
  actualCheckOut: string | null;
  status: string;
  nights: number;
}

/**
 * Paginated room history response
 */
export interface RoomHistoryResponse {
  roomNumber: string;
  stays: RoomHistoryEntry[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Fetch room directory from PMS
 * Returns all rooms enriched with current stay and guest info
 */
export async function fetchRoomDirectory(): Promise<RoomDirectoryEntry[]> {
  const url = `${API_BASE}/pms-sandbox/rooms`;

  console.log('[OPS_FEED] Fetching room directory');

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch room directory: ${response.statusText}`);
  }

  const data = await response.json();

  console.log(`[OPS_FEED] Loaded ${data.data?.length || 0} rooms`);

  return data.data || [];
}

/**
 * Search room stay history with filtering and pagination
 *
 * @param roomNumber - The room number to query history for
 * @param filters - Optional search/pagination params
 * @returns Paginated room history response
 */
export async function fetchRoomHistory(
  roomNumber: string,
  filters?: {
    guest?: string;
    from?: string;
    to?: string;
    page?: number;
    limit?: number;
  }
): Promise<RoomHistoryResponse> {
  const params = new URLSearchParams();

  if (filters?.guest) params.append('guest', filters.guest);
  if (filters?.from) params.append('from', filters.from);
  if (filters?.to) params.append('to', filters.to);
  if (filters?.page) params.append('page', filters.page.toString());
  if (filters?.limit) params.append('limit', filters.limit.toString());

  const url = `${API_BASE}/pms-sandbox/rooms/${roomNumber}/history${params.toString() ? '?' + params.toString() : ''}`;

  console.log('[OPS_FEED] Fetching room history:', { roomNumber, filters });

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch room history: ${response.statusText}`);
  }

  const data = await response.json();

  console.log(`[OPS_FEED] Room ${roomNumber} history: ${data.data?.total || 0} total stays, page ${data.data?.page || 1}`);

  return data.data;
}

/**
 * Staff edit: update status and/or metadata of an ORDER or REQUEST record
 */
export async function updateOrderRecord(
  id: string,
  updates: { status?: string; metadata?: Record<string, any> }
): Promise<void> {
  const url = `${API_BASE}/activity-ledger/${id}`;
  const response = await fetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Update failed: ${response.statusText}`);
  }
}

/**
 * Staff sends a message to a guest (appended to activity ledger as type=MESSAGE).
 * Guest app polls these records to show in inbox.
 */
export async function sendStaffMessage(params: {
  stayId: string;
  roomNumber: string;
  guestName?: string;
  subject: string;
  message: string;
  staffMember?: string;
  relatedOrderId?: string;
}): Promise<string> {
  const url = `${API_BASE}/activity-ledger/message`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Send message failed: ${response.statusText}`);
  }
  const data = await response.json();
  return data.id;
}
